// ===============================================================
// File Library — Browse
// Filterable card grid of all documents
// ===============================================================

import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Title, Text, SimpleGrid, Paper, Group, Badge, Select, TextInput,
  Center, Stack, ThemeIcon, Button,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconFileTypePdf, IconFileTypeDoc, IconFileTypeXls,
  IconFileTypePpt, IconPhoto, IconFile, IconMoodEmpty,
} from '@tabler/icons-react';
import { useFileLibraryStore } from '../../store/fileLibraryStore';
import type { FLDocument, FLCategory } from '../../store/fileLibraryStore';
import { semantic } from '../../lib/colors';

function getCatById(id: string, categories: FLCategory[]): FLCategory | undefined {
  return categories.find(c => String(c.id) === id || c.name === id);
}

function getCatColor(catId: string, categories: FLCategory[]): string {
  const cat = getCatById(catId, categories);
  return cat?.color || '#64748B';
}

function getCatName(catId: string, categories: FLCategory[]): string {
  const cat = getCatById(catId, categories);
  return cat?.name || catId || 'Uncategorized';
}

function getFileIcon(fileType: string) {
  const t = (fileType || '').toLowerCase();
  if (t === 'pdf') return IconFileTypePdf;
  if (t === 'doc' || t === 'docx') return IconFileTypeDoc;
  if (t === 'xls' || t === 'xlsx') return IconFileTypeXls;
  if (t === 'ppt' || t === 'pptx') return IconFileTypePpt;
  if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(t)) return IconPhoto;
  return IconFile;
}

type StatusTab = 'all' | 'published' | 'draft' | 'expired' | 'archived';
type SortOption = 'newest' | 'oldest' | 'az' | 'expiring';

export default function Browse() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { documents, categories, loading, loadDocuments, loadCategories } = useFileLibraryStore();

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(searchParams.get('category') || null);
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [statusTab, setStatusTab] = useState<StatusTab>('all');

  useEffect(() => {
    loadDocuments();
    loadCategories();
  }, []);

  useEffect(() => {
    const cat = searchParams.get('category');
    if (cat) setCategoryFilter(cat);
  }, [searchParams]);

  const now = new Date();
  const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  const getDocStatus = (doc: FLDocument): string => {
    if (doc.status === 'archived') return 'archived';
    if (doc.status === 'draft') return 'draft';
    if (doc.expirationDate && new Date(doc.expirationDate) < now) return 'expired';
    return doc.status || 'draft';
  };

  // Counts for status tabs
  const statusCounts = useMemo(() => {
    const counts: Record<StatusTab, number> = { all: 0, published: 0, draft: 0, expired: 0, archived: 0 };
    documents.forEach(d => {
      counts.all++;
      const s = getDocStatus(d);
      if (s === 'published') counts.published++;
      else if (s === 'draft') counts.draft++;
      else if (s === 'expired') counts.expired++;
      else if (s === 'archived') counts.archived++;
    });
    return counts;
  }, [documents]);

  const categoryOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [{ value: '', label: 'All Categories' }];
    for (const c of categories) {
      const key = String(c.id);
      if (!c.name || seen.has(key)) continue;
      seen.add(key);
      opts.push({ value: key, label: c.name });
    }
    return opts;
  }, [categories]);

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'az', label: 'A-Z' },
    { value: 'expiring', label: 'Expiring Soon' },
  ];

  const filtered = useMemo(() => {
    let list = [...documents];

    // Status filter
    if (statusTab !== 'all') {
      list = list.filter(d => getDocStatus(d) === statusTab);
    }

    // Category filter
    if (categoryFilter) {
      list = list.filter(d => d.category === categoryFilter);
    }

    // Search filter
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(d =>
        (d.title ?? '').toLowerCase().includes(q) ||
        (d.description ?? '').toLowerCase().includes(q) ||
        (d.tags ?? '').toLowerCase().includes(q)
      );
    }

    // Sort
    if (sortBy === 'newest') {
      list.sort((a, b) => (b.publishDate ?? '').localeCompare(a.publishDate ?? ''));
    } else if (sortBy === 'oldest') {
      list.sort((a, b) => (a.publishDate ?? '').localeCompare(b.publishDate ?? ''));
    } else if (sortBy === 'az') {
      list.sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));
    } else if (sortBy === 'expiring') {
      list.sort((a, b) => (a.expirationDate ?? '9999').localeCompare(b.expirationDate ?? '9999'));
    }

    return list;
  }, [documents, statusTab, categoryFilter, search, sortBy]);

  const getStatusIndicator = (doc: FLDocument): { color: string; label: string } => {
    const s = getDocStatus(doc);
    if (s === 'draft') return { color: '#9ca3af', label: 'Draft' };
    if (s === 'expired') return { color: semantic.error, label: 'Expired' };
    if (s === 'archived') return { color: '#6b7280', label: 'Archived' };
    // Check expiring soon
    if (doc.expirationDate) {
      const exp = new Date(doc.expirationDate);
      if (exp > now && exp <= thirtyDaysFromNow) {
        return { color: '#A67103', label: 'Expiring Soon' };
      }
    }
    return { color: '#07963F', label: 'Published' };
  };

  const STATUS_TABS: { key: StatusTab; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'published', label: 'Published' },
    { key: 'draft', label: 'Draft' },
    { key: 'expired', label: 'Expired' },
    { key: 'archived', label: 'Archived' },
  ];

  return (
    <div>
      <Title order={3} mb="md" c="black">Browse Documents</Title>

      {/* Toolbar */}
      <Group mb="sm" gap="sm" wrap="wrap">
        <TextInput
          placeholder="Search documents..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="xs"
          w={220}
        />
        <Select
          data={categoryOptions}
          value={categoryFilter || ''}
          onChange={v => setCategoryFilter(v || null)}
          size="xs"
          w={180}
          placeholder="Category"
          clearable
        />
        <Select
          data={sortOptions}
          value={sortBy}
          onChange={v => setSortBy((v as SortOption) || 'newest')}
          size="xs"
          w={160}
        />
      </Group>

      {/* Status Tabs */}
      <Group gap="xs" mb="md">
        {STATUS_TABS.map(tab => (
          <Button
            key={tab.key}
            variant={statusTab === tab.key ? 'filled' : 'subtle'}
            color={statusTab === tab.key ? '#64748B' : 'gray'}
            size="xs"
            onClick={() => setStatusTab(tab.key)}
          >
            {tab.label} ({statusCounts[tab.key]})
          </Button>
        ))}
      </Group>

      {/* Document Grid */}
      {loading ? (
        <Center p="xl"><Text c="dimmed">Loading...</Text></Center>
      ) : filtered.length === 0 ? (
        <Center py={60}>
          <Stack align="center" gap="xs">
            <ThemeIcon size={48} radius="xl" variant="light" color="gray">
              <IconMoodEmpty size={24} />
            </ThemeIcon>
            <Text fw={500} c="dimmed">No documents match your filters</Text>
            <Text size="xs" c="dimmed">Try adjusting your search or filters.</Text>
          </Stack>
        </Center>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
          {filtered.map(doc => {
            const FileIcon = getFileIcon(doc.fileType ?? '');
            const status = getStatusIndicator(doc);
            const tags = doc.tags ? doc.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
            const visibleTags = tags.slice(0, 4);
            const extraTagCount = Math.max(0, tags.length - 4);

            return (
              <Paper
                key={doc.id}
                p="sm"
                radius="sm"
                withBorder
                style={{ cursor: 'pointer', position: 'relative' }}
                onClick={() => navigate(`/filelibrary/doc/${doc.id}`)}
              >
                {/* Priority badge (top-right) */}
                {(doc.priority === 'important' || doc.priority === 'critical') && (
                  <Badge
                    size="xs"
                    color={doc.priority === 'critical' ? 'red' : 'orange'}
                    style={{ position: 'absolute', top: 8, right: 8 }}
                  >
                    {doc.priority}
                  </Badge>
                )}

                {/* Title */}
                <Group gap="xs" mb={4}>
                  <ThemeIcon size={22} variant="light" color="gray">
                    <FileIcon size={13} />
                  </ThemeIcon>
                  <Text size="sm" fw={600} style={{ flex: 1 }} lineClamp={1}>{doc.title}</Text>
                </Group>

                {/* Category badge */}
                <Badge
                  size="xs"
                  mb={6}
                  style={{ backgroundColor: getCatColor(doc.category ?? '', categories), color: '#fff' }}
                >
                  {getCatName(doc.category ?? '', categories)}
                </Badge>

                {/* Description */}
                <Text
                  size="xs"
                  c="dimmed"
                  mb={6}
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {doc.description || 'No description'}
                </Text>

                {/* Meta row */}
                <Group gap="xs" mb={6}>
                  <Text size="xs" c="dimmed">
                    Published: {doc.publishDate ? new Date(doc.publishDate).toLocaleDateString() : '—'}
                  </Text>
                  <Text size="xs" c="dimmed">
                    Expires: {doc.expirationDate ? new Date(doc.expirationDate).toLocaleDateString() : '—'}
                  </Text>
                  {doc.version && (
                    <Text size="xs" c="dimmed">v{doc.version}</Text>
                  )}
                </Group>

                {/* Status indicator */}
                <Badge size="xs" variant="light" mb={6} style={{ borderColor: status.color, color: status.color }}>
                  {status.label}
                </Badge>

                {/* Tags */}
                {visibleTags.length > 0 && (
                  <Group gap={4}>
                    {visibleTags.map(tag => (
                      <Badge key={tag} size="xs" variant="outline" color="gray">{tag}</Badge>
                    ))}
                    {extraTagCount > 0 && (
                      <Badge size="xs" variant="outline" color="gray">+{extraTagCount} more</Badge>
                    )}
                  </Group>
                )}
              </Paper>
            );
          })}
        </SimpleGrid>
      )}
    </div>
  );
}
